import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import { api, type Ajuste, type Categoria, type Institucion } from '@/lib/api';
import Topbar from '@/components/layout/Topbar';
import Dock from '@/components/layout/Dock';
import AssistantPanel from '@/components/AssistantPanel';
import RegistroModal from '@/components/RegistroModal';
import Panel from '@/components/sections/Panel';
import Plano from '@/components/sections/Plano';
import Territorio from '@/components/sections/Territorio';
import Seguimiento from '@/components/sections/Seguimiento';
import Ajustes from '@/components/sections/Ajustes';
import BrigadistaView from '@/components/BrigadistaView';
import ViviendaAdminView from '@/components/ViviendaAdminView';
import ViviendaSistView from '@/components/ViviendaSistView';
import DroneEquipoView from '@/components/DroneEquipoView';

export default function AppShell() {
  const { token, user, institucion, setInstitucion } = useAuthStore();
  const section = useAppStore((s) => s.section);
  const { viviendaModalOpen, cerrarVivienda, setDark } = useAppStore();
  const isBrigadista = user?.rol === 'brigadista';
  const isViviendaAdmin = user?.rol === 'admin_vivienda';
  const isViviendaSist = user?.rol === 'sistematizador_vivienda';
  const isDrone = user?.rol === 'operador_drone';

  const { data: ajustes } = useQuery<Ajuste>({
    queryKey: ['ajustes'],
    queryFn: () => api.ajustes(token!),
    enabled: !!token,
  });

  useEffect(() => {
    const dark = ajustes?.nocturno ?? false;
    document.documentElement.classList.toggle('dark', dark);
    setDark(dark);
  }, [ajustes?.nocturno, setDark]);

  const { data: cats = [] } = useQuery<Categoria[]>({
    queryKey: ['categorias'],
    queryFn: () => api.categorias(token!),
    enabled: !!token,
  });

  const { data: instituciones = [] } = useQuery<Institucion[]>({
    queryKey: ['instituciones'],
    queryFn: () => api.instituciones(token!),
    enabled: !!token,
  });

  // Bootstrap: set institution from user.institucionId or default to "todos"
  useEffect(() => {
    if (!token || institucion || instituciones.length === 0) return;
    const match = user?.institucionId
      ? instituciones.find(i => i.id === user.institucionId)
      : instituciones.find(i => i.slug === 'todos');
    if (match) setInstitucion(match);
  }, [token, institucion, instituciones, user, setInstitucion]);

  // Filter cats by institution
  const filteredCats = institucion?.categorias
    ? cats.filter(c => (institucion.categorias as string[]).includes(c.slug))
    : cats;

  if (isBrigadista) return <BrigadistaView />;
  if (isViviendaAdmin) return <ViviendaAdminView />;
  if (isViviendaSist) return <ViviendaSistView />;
  if (isDrone) return <DroneEquipoView />;

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden', background: 'var(--bg-shell)' }}>
      <Topbar cats={filteredCats} instituciones={instituciones} />

      {/* Section views */}
      <div style={{ position: 'absolute', inset: 0, animation: 'fadeSection 300ms ease-out' }} key={section}>
        {section === 'panel' && <Panel />}
        {section === 'plano' && <Plano />}
        {section === 'territorio' && <Territorio />}
        {section === 'seguimiento' && <Seguimiento />}
        {section === 'ajustes' && <Ajustes />}
      </div>

      <AssistantPanel />
      <Dock />
      <RegistroModal />

      {/* Modal de registro de vivienda */}
      {viviendaModalOpen && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) cerrarVivienda(); }}
          style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(5,15,35,0.65)', backdropFilter: 'blur(6px)', animation: 'popIn 200ms ease-out' }}
        >
          <div style={{ position: 'relative', width: 580, maxWidth: 'calc(100vw - 32px)', borderRadius: 22, overflow: 'hidden', boxShadow: '0 28px 64px rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {/* Botón cerrar */}
            <button
              onClick={cerrarVivienda}
              style={{ position: 'absolute', top: 14, right: 14, zIndex: 10, width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.7)', fontSize: 16, backdropFilter: 'blur(4px)' }}
            >
              <i className="ti ti-x" />
            </button>
            {/* Contenedor del formulario */}
            <div style={{ position: 'relative', height: '86vh', overflowY: 'auto' }}>
              <ViviendaSistView onClose={cerrarVivienda} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
